import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ADULT_CATEGORIES, FAMILY_CATEGORIES } from "@/lib/ageCategories";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlignJustify, CalendarDays, LayoutGrid, Users, User, Shield, LogOut, ChevronRight, Layers } from "lucide-react";

function fmtDate(a) {
  const opt = { day: "numeric", month: "long" };
  const s = a.start_date ? new Date(a.start_date + "T00:00:00") : null;
  if (!s) return "";
  if (a.end_date && a.end_date !== a.start_date) {
    const e = new Date(a.end_date + "T00:00:00");
    return `${s.toLocaleDateString("es-ES", opt)} – ${e.toLocaleDateString("es-ES", opt)}`;
  }
  return s.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
}

export function NavMenu() {
  const [open, setOpen] = useState(false);
  const [acts, setActs] = useState([]);
  const navigate = useNavigate();
  const { exitAccess, user, logoutUser } = useApp();

  useEffect(() => {
    if (open) api.get("/activities").then((r) => setActs(r.data)).catch(() => {});
  }, [open]);

  const go = (path) => { setOpen(false); navigate(path); };
  const itemCls = "w-full flex items-center gap-3 p-3 rounded-2xl font-bold text-slate-700 hover:bg-[#003284]/5 transition-colors";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="menu-btn" className="flex items-center gap-2 rounded-full border-2 border-[#003284] text-[#003284] font-bold px-4 py-2 text-sm hover:bg-[#003284] hover:text-white transition-colors">
          <AlignJustify size={18} /> <span>Menú</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[88vw] sm:w-[400px] sm:max-w-none bg-white overflow-y-auto p-0">
        <div className="p-6">
          <SheetHeader><SheetTitle className="brand-navy text-2xl font-black">Menú</SheetTitle></SheetHeader>

          <nav className="mt-6 space-y-1">
            <button data-testid="menu-actividades" onClick={() => go("/inicio")} className={itemCls}>
              <span className="w-9 h-9 rounded-xl bg-[#003284] text-white flex items-center justify-center"><LayoutGrid size={18} /></span>
              Ver actividades
            </button>
            <button data-testid="menu-calendario" onClick={() => go("/calendario")} className={itemCls}>
              <span className="w-9 h-9 rounded-xl bg-[#003284] text-white flex items-center justify-center"><CalendarDays size={18} /></span>
              Ver calendario
            </button>
            <button data-testid="menu-cuenta" onClick={() => go("/cuenta")} className={itemCls}>
              <span className="w-9 h-9 rounded-xl bg-[#FF7F27] text-white flex items-center justify-center"><User size={18} /></span>
              {user ? "Mi cuenta" : "Iniciar sesión / Registrarse"}
            </button>
          </nav>

          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 px-1 flex items-center gap-1.5"><Users size={13} /> Ver por edades</p>
            <div className="space-y-3">
              <div className="rounded-2xl border-2 border-[#003284]/25 bg-[#003284]/[0.05] p-3">
                <div className="flex items-center gap-2 mb-2.5">
                  <Users size={16} className="text-[#003284]" />
                  <span className="text-xs font-black uppercase tracking-wide text-[#003284]">Adultos</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ADULT_CATEGORIES.map((c) => (
                    <button key={c.id} data-testid={`menu-edad-${c.id}`} onClick={() => go(`/inicio?edad=${c.id}`)} className="rounded-full px-3 py-1.5 text-sm font-bold border-2 border-slate-200 text-slate-600 hover:border-[#003284] hover:text-[#003284] transition-colors">{c.label} <span className="opacity-60">{c.range}</span></button>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border-2 border-[#FF7F27]/35 bg-[#FF7F27]/[0.06] p-3">
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="flex items-end text-[#FF7F27] -space-x-0.5"><User size={18} strokeWidth={2.25} /><User size={12} strokeWidth={2.25} /></span>
                  <span className="text-xs font-black uppercase tracking-wide text-[#FF7F27]">Familiares</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="menu-edad-familiares" onClick={() => go(`/inicio?edad=familiares`)} className="rounded-full px-3 py-1.5 text-sm font-bold border-2 border-slate-200 text-slate-600 hover:border-[#FF7F27] hover:text-[#FF7F27] transition-colors">Todas las familiares</button>
                  {FAMILY_CATEGORIES.map((c) => (
                    <button key={c.id} data-testid={`menu-edad-${c.id}`} onClick={() => go(`/inicio?edad=${c.id}`)} className="rounded-full px-3 py-1.5 text-sm font-bold border-2 border-slate-200 text-slate-600 hover:border-[#FF7F27] hover:text-[#FF7F27] transition-colors">{c.label} <span className="opacity-60">{c.range}</span></button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 px-1 flex items-center gap-1.5"><Layers size={13} /> Tipo de actividad</p>
            <div className="flex flex-wrap gap-2">
              {[...new Set(acts.map((a) => a.activity_type).filter(Boolean))].map((t) => (
                <button key={t} data-testid={`menu-tipo-${t}`} onClick={() => go(`/inicio?tipo=${encodeURIComponent(t)}`)} className="rounded-full px-3.5 py-1.5 text-sm font-bold border-2 border-slate-200 text-slate-600 hover:border-[#003284] hover:text-[#003284] transition-colors">{t}</button>
              ))}
              {[...new Set(acts.map((a) => a.activity_type).filter(Boolean))].length === 0 && <p className="text-sm text-slate-400 px-1">Sin categorías todavía.</p>}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 px-1">Todas las actividades</p>
            <div className="space-y-2">
              {acts.length === 0 && <p className="text-sm text-slate-400 px-1">No hay actividades.</p>}
              {acts.map((a) => (
                <button key={a.id} data-testid={`menu-act-${a.id}`} onClick={() => go(`/actividad/${a.id}`)} className="w-full text-left flex items-center gap-3 p-2 rounded-2xl hover:bg-slate-50 transition-colors group">
                  <img src={a.photos?.[0]} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm brand-navy leading-tight truncate">{a.title}</p>
                    <p className="text-xs text-slate-400 capitalize truncate">{fmtDate(a)}</p>
                    {a.status === "cancelada" ? <span className="text-[10px] font-bold text-[#E31837]">Cancelada</span>
                      : a.is_full ? <span className="text-[10px] font-bold text-slate-500">Completa</span>
                        : null}
                  </div>
                  <ChevronRight size={16} className="text-slate-300 group-hover:text-[#FF7F27] shrink-0" />
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 border-t pt-4 space-y-1">
            {user && <p className="px-3 pb-1 text-sm text-slate-500">Sesión: <span className="font-bold brand-navy">{user.name}</span></p>}
            {user && <button data-testid="menu-logout-user" onClick={() => { logoutUser(); setOpen(false); }} className="w-full flex items-center gap-3 p-3 rounded-2xl font-semibold text-slate-600 hover:bg-slate-50 transition-colors"><LogOut size={18} /> Cerrar sesión</button>}
            <button data-testid="menu-admin" onClick={() => go("/admin")} className="w-full flex items-center gap-3 p-3 rounded-2xl font-semibold text-slate-600 hover:bg-slate-50 transition-colors"><Shield size={18} /> Administración</button>
            <button data-testid="menu-exit" onClick={() => { exitAccess(); go("/"); }} className="w-full flex items-center gap-3 p-3 rounded-2xl font-semibold text-[#E31837] hover:bg-red-50 transition-colors"><LogOut size={18} /> Salir</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
